import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { Subscription, interval } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { NotificationService } from 'src/@core/services/notification.service';
import { openPresignedDownload } from 'src/@core/utils/presigned-download.util';
import { AgentEvalsService } from '../agent-evals.service';
import {
  AgentEvalRun,
  AgentEvalSamplePreview,
  AgentEvalTest,
} from '../agent-evals.types';

export interface RunEvalsDialogData {
  entityId: string;
  projectId: string;
  tests: AgentEvalTest[];
}

@Component({
  selector: 'hour4u-admin-run-agent-evals-dialog',
  templateUrl: './run-evals-dialog.component.html',
  styleUrls: ['./run-evals-dialog.component.scss'],
})
export class RunAgentEvalsDialogComponent implements OnInit, OnDestroy {
  readonly allCap = 10000;
  mode: 'buckets' | 'all' | 'lt_60s' | 'm1_to_2' | 'gt_2m' = 'buckets';
  limits = { lt_60s: 10, m1_to_2: 25, gt_2m: 50, all: 10000 };
  preview: AgentEvalSamplePreview | null = null;
  loadingPreview = false;
  starting = false;
  activeRun: AgentEvalRun | null = null;
  selectAllTests = true;
  selectedTestIds: { [id: string]: boolean } = {};
  humanReviewedOnly = false;
  private pollSub: Subscription;

  constructor(
    private dialogRef: MatDialogRef<RunAgentEvalsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RunEvalsDialogData,
    private agentEvalsService: AgentEvalsService,
    private notify: NotificationService
  ) {
    (data.tests || []).forEach((t) => {
      if (t && t.test_id) {
        this.selectedTestIds[t.test_id] = true;
      }
    });
  }

  ngOnInit(): void {
    this.reloadPreview();
  }

  ngOnDestroy(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
    }
  }

  get tests(): AgentEvalTest[] {
    return (this.data && this.data.tests) || [];
  }

  selectedTestCount(): number {
    if (this.selectAllTests) {
      return this.tests.length;
    }
    return this.tests.filter((t) => t.test_id && this.selectedTestIds[t.test_id]).length;
  }

  selectedTestIdList(): string[] {
    if (this.selectAllTests) {
      return this.tests.map((t) => t.test_id).filter(Boolean);
    }
    return this.tests
      .filter((t) => t.test_id && this.selectedTestIds[t.test_id])
      .map((t) => t.test_id);
  }

  onSelectAllChange(): void {
    if (this.selectAllTests) {
      this.tests.forEach((t) => {
        if (t.test_id) {
          this.selectedTestIds[t.test_id] = true;
        }
      });
    }
  }

  setMode(mode: 'buckets' | 'all' | 'lt_60s' | 'm1_to_2' | 'gt_2m'): void {
    if (this.mode === mode) {
      return;
    }
    this.mode = mode;
    this.reloadPreview();
  }

  onHumanReviewedOnlyChange(): void {
    this.reloadPreview();
  }

  onAllCapChange(): void {
    if (this.limits.all > this.allCap) {
      this.limits.all = this.allCap;
    }
    if (this.limits.all < 1) {
      this.limits.all = 1;
    }
    this.reloadPreview();
  }

  reloadPreview(): void {
    this.loadingPreview = true;
    this.agentEvalsService
      .previewSample(
        this.data.entityId,
        this.data.projectId,
        this.limits,
        this.humanReviewedOnly
      )
      .subscribe(
        (preview) => {
          this.preview = preview;
          this.loadingPreview = false;
        },
        (err) => {
          this.loadingPreview = false;
          const msg = (err && err.error && err.error.message) || 'Sample preview failed';
          this.notify.showMessage(msg, 4000);
        }
      );
  }

  uniqueClientCount(): number {
    if (!this.preview) {
      return 0;
    }
    if (this.preview.unique_clients != null) {
      return this.preview.unique_clients;
    }
    return (this.preview.available && this.preview.available.all) || 0;
  }

  totalCallCount(): number {
    if (!this.preview) {
      return 0;
    }
    if (this.preview.total_calls != null) {
      return this.preview.total_calls;
    }
    if (this.preview.available_calls) {
      return this.preview.available_calls.all || 0;
    }
    return this.uniqueClientCount();
  }

  selectedClientCount(): number {
    if (!this.preview || !this.preview.selected_counts) {
      return 0;
    }
    if (this.mode === 'all') {
      return this.preview.selected_counts.all || 0;
    }
    if (this.mode === 'buckets') {
      return (
        (this.preview.selected_counts.lt_60s || 0) +
        (this.preview.selected_counts.m1_to_2 || 0) +
        (this.preview.selected_counts.gt_2m || 0)
      );
    }
    return this.preview.selected_counts[this.mode] || 0;
  }

  selectedCallCount(): number {
    if (!this.preview) {
      return 0;
    }
    const calls = this.preview.selected_calls;
    if (calls) {
      if (this.mode === 'all') {
        return calls.all || 0;
      }
      if (this.mode === 'buckets') {
        return (calls.lt_60s || 0) + (calls.m1_to_2 || 0) + (calls.gt_2m || 0);
      }
      return calls[this.mode] || 0;
    }
    // Older API: selected_counts were call counts.
    return this.selectedClientCount();
  }

  estimatedCredits(): number {
    if (!this.preview) {
      return 0;
    }
    const rate = this.preview.credits_per_score || 1;
    // Excel is one row per unique client; credits follow Excel rows.
    return this.selectedTestCount() * this.selectedClientCount() * rate;
  }

  progressPct(): number {
    if (!this.activeRun || !this.activeRun.progress) {
      return 0;
    }
    const total = this.activeRun.progress.tests_total || this.selectedTestCount() || 0;
    const done = this.activeRun.progress.tests_done || 0;
    if (!total) {
      return 0;
    }
    return Math.min(100, Math.round((done / total) * 100));
  }

  start(): void {
    const testIds = this.selectedTestIdList();
    if (!testIds.length) {
      this.notify.showMessage('Select at least one test to run', 3000);
      return;
    }
    this.starting = true;
    this.agentEvalsService
      .startRun({
        entity_id: this.data.entityId,
        project_id: this.data.projectId,
        mode: this.mode,
        limits: this.limits,
        test_ids: testIds,
        human_reviewed_only: this.humanReviewedOnly,
      })
      .subscribe(
        (res: any) => {
          this.starting = false;
          const runId = res && res.run_id;
          if (!runId) {
            this.notify.showMessage('Run started but no run_id returned', 4000);
            return;
          }
          this.activeRun = {
            run_id: runId,
            project_id: this.data.projectId,
            entity_id: this.data.entityId,
            status: 'queued',
            credits_estimated: res.credits_estimated,
            progress: { tests_done: 0, tests_total: testIds.length },
          };
          this.poll(runId);
        },
        (err) => {
          this.starting = false;
          const msg = (err && err.error && err.error.message) || 'Failed to start run';
          this.notify.showMessage(msg, 4000);
        }
      );
  }

  private poll(runId: string): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
    }
    this.pollSub = interval(2500)
      .pipe(
        switchMap(() =>
          this.agentEvalsService.getRunStatus(
            this.data.entityId,
            this.data.projectId,
            runId
          )
        ),
        takeWhile(
          (run) => !run || ['queued', 'running'].indexOf(run.status) >= 0,
          true
        )
      )
      .subscribe(
        (run) => {
          this.activeRun = run;
          if (run && ['completed', 'failed', 'cancelled'].indexOf(run.status) >= 0) {
            if (run.status === 'completed') {
              this.notify.showMessage('Agent Evals run completed', 3000);
            }
          }
        },
        () => {
          this.notify.showMessage('Lost connection while polling run status', 4000);
        }
      );
  }

  downloadReport(): void {
    const run = this.activeRun;
    const url = (run && (run.report_download_url || run.report_url)) || '';
    if (!url) {
      this.notify.showMessage('Report URL not available yet', 3000);
      return;
    }
    openPresignedDownload(url, `agent-evals-${run.run_id || 'report'}.html`);
  }

  cancel(): void {
    if (!this.activeRun || !this.activeRun.run_id) {
      return;
    }
    this.agentEvalsService
      .cancelRun(this.data.entityId, this.data.projectId, this.activeRun.run_id)
      .subscribe(
        (res: any) => {
          this.activeRun = (res && res.data) || this.activeRun;
        },
        () => this.notify.showMessage('Cancel request failed', 3000)
      );
  }

  close(): void {
    this.dialogRef.close(this.activeRun);
  }
}
